import { Link, createFileRoute } from '@tanstack/react-router';
import { Archive, Dumbbell, LayoutTemplate, ListChecks, Plus } from 'lucide-react';

import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { EMPTY_ACTION_CLASS, EMPTY_SECONDARY_CLASS } from '@/components/emptyStateClasses';
import { ErrorBanner } from '@/components/ErrorBanner';
import { PageHeader } from '@/components/PageHeader';
import { Spinner } from '@/components/Spinner';
import { Chip } from '@/components/exercise/Chip';
import { RoutineCard } from '@/components/routine/RoutineCard';
import { RoutineTemplates } from '@/components/routine/RoutineTemplates';
import { useRoutineList } from '@/lib/queries/routines';

interface RoutinesSearch {
  archived: boolean;
}

export const Route = createFileRoute('/_app/routines/')({
  validateSearch: (search: Record<string, unknown>): RoutinesSearch => ({
    archived: search['archived'] === true,
  }),
  component: RoutinesPage,
});

function RoutinesPage() {
  const { archived } = Route.useSearch();
  const navigate = Route.useNavigate();
  const query = useRoutineList({ includeArchived: archived });

  const routines = query.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <>
      <PageHeader
        title="Mis rutinas"
        subtitle="Tus rutinas de entrenamiento: edítalas o copia una plantilla."
        action={
          <Link
            to="/routines/new"
            aria-label="Nueva rutina"
            className="flex size-11 items-center justify-center rounded-xl bg-brand text-brand-ink"
          >
            <Plus className="size-5" aria-hidden="true" />
          </Link>
        }
      />

      <div className="mb-4 flex items-center gap-2">
        <Chip
          selected={archived}
          className="min-h-11"
          onClick={() => void navigate({ search: { archived: !archived }, replace: true })}
        >
          Mostrar archivadas
        </Chip>
      </div>

      {query.isPending ? (
        <div className="flex items-center gap-3 rounded-2xl border border-line bg-surface-raised p-4 text-ink-muted">
          <Spinner size="sm" />
          <span className="text-sm">Cargando tus rutinas…</span>
        </div>
      ) : query.isError ? (
        <ErrorBanner
          title="No hemos podido cargar las rutinas"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : routines.length === 0 ? (
        archived ? (
          <EmptyState
            icon={Archive}
            title="No tienes rutinas archivadas"
            description="Cuando archives una rutina que ya no sigues, la encontrarás aquí."
          />
        ) : (
          <EmptyState
            icon={ListChecks}
            title="Aún no tienes rutinas"
            description="Copia una plantilla para empezar: ya trae sus días y ejercicios, y luego puedes cambiarla. O crea la tuya desde cero."
            action={
              <>
                <a href="#plantillas" className={EMPTY_ACTION_CLASS}>
                  <LayoutTemplate className="size-4" aria-hidden="true" />
                  Ver plantillas
                </a>
                <Link to="/routines/new" className={EMPTY_SECONDARY_CLASS}>
                  <Plus className="size-4" aria-hidden="true" />
                  Crear desde cero
                </Link>
              </>
            }
          />
        )
      ) : (
        <ul className="flex flex-col gap-2">
          {routines.map((routine) => (
            <RoutineCard key={routine.id} routine={routine} />
          ))}
        </ul>
      )}

      {query.hasNextPage ? (
        <Button
          variant="secondary"
          block
          className="mt-3"
          loading={query.isFetchingNextPage}
          onClick={() => void query.fetchNextPage()}
        >
          Cargar más
        </Button>
      ) : null}

      {archived ? null : (
        <div className="mt-6">
          <RoutineTemplates />
        </div>
      )}

      <div className="mt-6 mb-2">
        <Link
          to="/exercises"
          search={{}}
          className="flex min-h-14 items-center gap-3 rounded-2xl border border-line bg-surface-raised px-4 transition-colors hover:bg-surface-overlay"
        >
          <Dumbbell className="size-5 text-brand" aria-hidden="true" />
          <span className="text-sm font-medium text-ink">Ir al catálogo de ejercicios</span>
        </Link>
      </div>
    </>
  );
}
